/**
 * Saudi company settings — legal fields, geo, banking, validation.
 */
import { SAUDI_BANKS } from '../../utils/bankingHelpers';
import { SAUDI_CITIES, SAUDI_REGIONS } from '../../../pages/ressources-humaines/utils/hrSaudiFormat';
import {
  formatTRN,
  validateTRN,
  TRN_LENGTH,
  SAUDI_VAT_RATE,
} from '../../../pages/comptabilite/utils/saudiTaxConfig';
import {
  formatSaudiIbanDisplay,
  normalizeSaudiIban,
  SAUDI_IBAN_PLACEHOLDER,
  validateSaudiIban,
} from '../../utils/ibanValidation';
import {
  getBrandPhonePlaceholder,
  isBrandPhoneValid,
  normalizeBrandPhone,
} from '../../utils/brandCountry';
import {
  getBrandCurrencyOptions,
  getBrandDefaultCurrency,
  getBrandVatRate,
  SAUDI_CURRENCY_SYMBOL,
} from '../../utils/brandCurrency';

export {
  SAUDI_BANKS,
  formatTRN,
  validateTRN,
  TRN_LENGTH,
  SAUDI_VAT_RATE,
  formatSaudiIbanDisplay,
  normalizeSaudiIban,
  SAUDI_IBAN_PLACEHOLDER,
  validateSaudiIban,
  getBrandPhonePlaceholder,
  isBrandPhoneValid,
  normalizeBrandPhone,
  getBrandCurrencyOptions,
  getBrandDefaultCurrency,
  getBrandVatRate,
  SAUDI_CURRENCY_SYMBOL,
};

export const SAUDI_LEGAL_FORMS = [
  { value: 'LLC', label: 'شركة ذات مسؤولية محدودة (LLC)' },
  { value: 'JSC', label: 'شركة مساهمة (JSC)' },
  { value: 'JSC_CLOSED', label: 'شركة مساهمة مقفلة' },
  { value: 'PARTNERSHIP', label: 'شركة تضامن' },
  { value: 'SOLE_PROP', label: 'مؤسسة فردية' },
  { value: 'BRANCH', label: 'فرع شركة أجنبية' },
  { value: 'PROFESSIONAL', label: 'شركة مهنية' },
];

export const SAUDI_REGION_OPTIONS = SAUDI_REGIONS.map((name) => ({
  value: name,
  label: name,
}));

export const SAUDI_CITY_OPTIONS = SAUDI_CITIES.map((name) => ({
  value: name,
  label: name,
}));

export const SAUDI_VAT_CATEGORIES = [
  { value: 'standard', rate: 15, labelKey: 'vat_category_standard' },
  { value: 'zero', rate: 0, labelKey: 'vat_category_zero' },
  { value: 'exempt', rate: null, labelKey: 'vat_category_exempt' },
];

export const SAUDI_FISCAL_YEAR_OPTIONS = [
  { value: 'gregorian', labelKey: 'fiscal_gregorian' },
  { value: 'hijri', labelKey: 'fiscal_hijri' },
];

/** VAT registration threshold (SAR/year) — mandatory above this revenue. */
export const SAUDI_VAT_REGISTRATION_THRESHOLD = 375_000;

export function validateSaudiVatNumber(value) {
  const cleaned = String(value || '').replace(/\D/g, '');
  if (!cleaned) return { valid: true, value: '' };
  if (cleaned.length !== TRN_LENGTH) {
    return { valid: false, message: 'vat_invalid_length' };
  }
  if (!cleaned.startsWith('3')) {
    return { valid: false, message: 'vat_invalid_prefix' };
  }
  if (!validateTRN(cleaned)) {
    return { valid: false, message: 'vat_invalid_format' };
  }
  return { valid: true, value: cleaned };
}

export function validateSaudiPostalCode(value) {
  const cleaned = String(value || '').replace(/\D/g, '');
  if (!cleaned) return { valid: true, value: '' };
  if (!/^\d{5}$/.test(cleaned)) {
    return { valid: false, message: 'postal_invalid' };
  }
  return { valid: true, value: cleaned };
}

export function defaultSaudiBankSlot() {
  return {
    iban: '',
    bank: '',
    branch: '',
    swift: '',
    wpsEnabled: false,
  };
}

/** Normalize stored bank row (object or legacy string). */
export function normalizeBankAccountRecord(record) {
  if (!record) return defaultSaudiBankSlot();
  if (typeof record === 'object' && !Array.isArray(record)) {
    const iban = record.iban || record.account || '';
    const bankCode = record.bank || '';
    const bankMeta = SAUDI_BANKS.find((b) => b.code === bankCode);
    return {
      iban: normalizeSaudiIban(iban),
      bank: bankCode,
      branch: record.branch || '',
      swift: record.swift || bankMeta?.swift || '',
      wpsEnabled: Boolean(record.wpsEnabled),
    };
  }

  const str = String(record).trim();
  if (!str) return defaultSaudiBankSlot();

  const ibanMatch = str.match(/SA\d{22}/i);
  if (ibanMatch) {
    const iban = normalizeSaudiIban(ibanMatch[0]);
    const bank = SAUDI_BANKS.find((b) => str.includes(b.code))?.code || '';
    return { ...defaultSaudiBankSlot(), iban, bank };
  }

  return { ...defaultSaudiBankSlot(), iban: str.replace(/\s/g, '').toUpperCase() };
}

export function formatBankAccountLegacyString(accountObj) {
  if (!accountObj?.iban) return '';
  const parts = [formatSaudiIbanDisplay(accountObj.iban)];
  if (accountObj.bank) parts.push(accountObj.bank);
  if (accountObj.branch) parts.push(accountObj.branch);
  return parts.join(' ').trim();
}

/** Map legacy Algerian company keys to Saudi fields (read-only migration helper). */
export function resolveCompanyField(company, saudiKey, legacyKeys = []) {
  if (company?.[saudiKey]) return company[saudiKey];
  for (const key of legacyKeys) {
    if (company?.[key]) return company[key];
  }
  return '';
}
